import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const AdminManagement = () => {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [open, setOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    tenantId: '',
    role: 'Admin'
  });

  useEffect(() => {
    fetchTenants();
    fetchAdmins();
  }, []);

  const fetchTenants = async () => {
    try {
      const response = await fetch('/api/tenants', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTenants(data);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
      setError('Failed to load tenants');
    }
  };

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      // This endpoint needs to be created in the backend
      const response = await fetch('/api/auth/admin-users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdmins(data);
      } else {
        console.error('Failed to load admin users - Response:', response.status, response.statusText);
        setError('Failed to load admin users');
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      setError('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (admin = null) => {
    if (admin) {
      setEditingAdmin(admin);
      setFormData({
        firstName: admin.firstName,
        lastName: admin.lastName,
        email: admin.email,
        password: '',
        tenantId: admin.tenantId,
        role: admin.role || 'Admin'
      });
    } else {
      setEditingAdmin(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        tenantId: '',
        role: 'Admin'
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingAdmin(null);
    setError('');
    setSuccess('');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const url = editingAdmin 
        ? `/api/auth/admin-users/${editingAdmin.id}` 
        : '/api/auth/create-admin';
      
      const method = editingAdmin ? 'PUT' : 'POST';
      
      // Transform data to match DTO property names (Pascal case)
      const requestData = {
        FirstName: formData.firstName,
        LastName: formData.lastName,
        Email: formData.email,
        Password: formData.password,
        TenantId: parseInt(formData.tenantId),
        Role: formData.role
      };

      console.log('Sending request data:', requestData);

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        setSuccess(editingAdmin ? 'Admin updated successfully' : 'Admin created successfully');
        fetchAdmins();
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        console.log('Response status:', response.status);
        const errorText = await response.text();
        console.log('Error response:', errorText);
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText };
        }
        
        // Handle detailed validation errors
        let errorMessage = errorData.message || errorData.title || 'Failed to save admin';
        
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const validationErrors = errorData.errors.map(err => err.description).join(', ');
          errorMessage = `${errorData.message}: ${validationErrors}`;
        }
        
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Error saving admin:', error);
      setError('Failed to save admin');
    }
  };

  const handleDelete = async (adminId) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) {
      return;
    }

    try {
      const response = await fetch(`/api/auth/admin-users/${adminId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        setSuccess('Admin deleted successfully');
        fetchAdmins();
      } else {
        setError('Failed to delete admin');
      }
    } catch (error) {
      console.error('Error deleting admin:', error);
      setError('Failed to delete admin');
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Admin Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
          >
            Create New Admin
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Paper sx={{ p: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.id}>
                    <TableCell>{`${admin.firstName} ${admin.lastName}`}</TableCell>
                    <TableCell>{admin.email}</TableCell>
                    <TableCell>{admin.tenantName}</TableCell>
                    <TableCell>
                      <Chip 
                        label={admin.role} 
                        color={admin.role === 'SuperAdmin' ? 'error' : 'primary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={admin.isActive ? 'Active' : 'Inactive'} 
                        color={admin.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpen(admin)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      {admin.role !== 'SuperAdmin' && (
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(admin.id)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {admins.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No admin users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingAdmin ? 'Edit Admin' : 'Create New Admin'}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
              
              <TextField
                autoFocus
                margin="dense"
                name="firstName"
                label="First Name"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
              
              <TextField
                margin="dense"
                name="lastName"
                label="Last Name"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
              
              <TextField
                margin="dense"
                name="email"
                label="Email"
                type="email"
                fullWidth
                variant="outlined"
                value={formData.email}
                onChange={handleChange}
                required
              />
              
              {!editingAdmin && (
                <TextField
                  margin="dense"
                  name="password"
                  label="Password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  value={formData.password}
                  onChange={handleChange}
                  required={!editingAdmin}
                  helperText="Must contain at least one uppercase letter, one lowercase letter, one digit, and one special character. Minimum 6 characters."
                />
              )}
              
              {editingAdmin && (
                <TextField
                  margin="dense"
                  name="password"
                  label="New Password (optional)"
                  type="password"
                  fullWidth
                  variant="outlined"
                  value={formData.password}
                  onChange={handleChange}
                  helperText="Leave blank to keep current password. If changing: Must contain uppercase, lowercase, digit, and special character."
                />
              )}
              
              <FormControl fullWidth margin="dense">
                <InputLabel>Tenant</InputLabel>
                <Select
                  name="tenantId"
                  value={formData.tenantId}
                  onChange={handleChange}
                  label="Tenant"
                  required
                >
                  {tenants.map((tenant) => (
                    <MenuItem key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth margin="dense">
                <InputLabel>Role</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  label="Role"
                  required
                >
                  <MenuItem value="Admin">Admin</MenuItem>
                  <MenuItem value="User">User</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Cancel</Button>
              <Button type="submit" variant="contained">
                {editingAdmin ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Box>
    </Container>
  );
};

export default AdminManagement;